import { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  User,
  Users,
  CalendarClock,
  MessageSquare,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card } from '@/components/ui/card';
import { cn, formatDateTime } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { getFormTypeName } from '../../../../shared/form-config';
import type { FormType } from '../../../../shared/form-types';

// ── Columnas del Kanban (estado propio de la plataforma, independiente de Notion) ──
type ColumnKey = 'todo' | 'in_progress' | 'waiting' | 'done';

// Estático con labelKey (no `t()`): COLUMN_KEYS lo consume a nivel de módulo, así
// que tiene que existir sin `t`. El componente traduce al usarlo (patrón DTALK_STATUS).
const COLUMN_DEFS: { key: ColumnKey; labelKey: string; dot: string }[] = [
  { key: 'todo', labelKey: 'admin.userrequestskanban.por_hacer', dot: 'bg-blue-500' },
  { key: 'in_progress', labelKey: 'admin.userrequestskanban.en_curso', dot: 'bg-yellow-500' },
  { key: 'waiting', labelKey: 'admin.userrequestskanban.en_espera', dot: 'bg-orange-500' },
  { key: 'done', labelKey: 'admin.userrequestskanban.cerrado', dot: 'bg-green-500' },
];

const COLUMN_KEYS = new Set<string>(COLUMN_DEFS.map((c) => c.key));

const HIDE_CLOSED_KEY = 'kanban-pedidos-hide-closed';

// Ubicación inicial de una card que aún no fue movida en el Kanban:
// derivar de su estado de Notion / DynamoDB. Una vez movida, manda `kanbanEstado`.
function columnForTicket(ticket: any): ColumnKey {
  if (ticket.kanbanEstado && COLUMN_KEYS.has(ticket.kanbanEstado)) {
    return ticket.kanbanEstado as ColumnKey;
  }
  const s = String(ticket.notionEstado ?? ticket.status ?? '').toLowerCase();
  if (s === 'en curso' || s === 'en pruebas') return 'in_progress';
  if (
    s === 'esperando aprobación' ||
    s === 'en espera (prioridades)' ||
    s === 'pendiente por tech' ||
    s === 'pendiente por el reporter'
  )
    return 'waiting';
  if (s === 'completado' || s === 'resuelto' || s === 'resolved' || s === 'no se corregirá' || s === 'cancelled')
    return 'done';
  return 'todo'; // abierto, pendiente, open, y default
}

function priorityClass(priority: string): string {
  const p = (priority ?? '').toLowerCase();
  if (p === 'crítica' || p === 'critica')
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
  if (p === 'alta')
    return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400';
  if (p === 'media')
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
}

// camelCase / snake_case / kebab → "Texto Legible"
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

// El "cuerpo" del ticket: description si es texto real (no el volcado JSON del form)
function ticketBody(ticket: any): string {
  const desc = String(ticket.description ?? '').trim();
  return desc && !desc.startsWith('{') ? desc : '';
}

// Campos del formulario, aplanados a [label, valor] para mostrar tipo Notion
function ticketFields(ticket: any): [string, string][] {
  const fd = ticket.formData;
  if (!fd || typeof fd !== 'object') return [];
  return Object.entries(fd)
    .filter(([, v]) => v != null && v !== '' && (typeof v !== 'object' || Array.isArray(v)))
    .map(([k, v]) => [humanizeKey(k), Array.isArray(v) ? v.join(', ') : String(v)] as [string, string]);
}

// Color del badge según el estado de Notion (mismo criterio que MyTicketsPage)
function notionStatusColor(status: string): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'abierto' || s === 'pendiente') return 'bg-blue-500';
  if (s === 'en curso') return 'bg-yellow-500';
  if (s === 'esperando aprobación' || s === 'en espera (prioridades)') return 'bg-orange-500';
  if (s === 'pendiente por tech' || s === 'pendiente por el reporter') return 'bg-amber-400';
  if (s === 'en pruebas') return 'bg-purple-500';
  if (s === 'completado' || s === 'resuelto') return 'bg-green-500';
  if (s === 'no se corregirá') return 'bg-gray-400';
  return 'bg-gray-500';
}

// ── Detalle en vivo desde Notion (estado actual + comentarios) ──
// Sólo se monta cuando la card está expandida → fetch lazy, 1 llamada por card.
function NotionDetail({ notionPageId }: { notionPageId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = trpc.tickets.getTicketNotionDetail.useQuery(
    { notionPageId },
    { staleTime: 60_000, refetchOnWindowFocus: false },
  );

  if (isLoading) {
    return <p className="text-[11px] text-muted-foreground">{t('admin.userrequestskanban.cargando_estado_y_comentarios_de_notion')}</p>;
  }
  if (!data) return null;

  return (
    <div className="space-y-2">
      {data.status && (
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">{t('admin.userrequestskanban.estado_en_notion')}</span>
          <span
            className={cn('inline-block text-white text-[10px] px-1.5 py-0.5 rounded', notionStatusColor(data.status))}
          >
            {data.status}
          </span>
        </div>
      )}

      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <MessageSquare className="h-3 w-3" /> {t('admin.userrequestskanban.comentarios')}{data.comments.length})
        </p>
        {data.comments.length === 0 ? (
          <p className="text-[11px] text-muted-foreground/60">{t('admin.userrequestskanban.sin_comentarios_en_notion')}</p>
        ) : (
          <ul className="space-y-1.5">
            {data.comments.map((c, i) => (
              <li key={i} className="text-[11px] bg-muted/50 rounded p-2">
                <div className="flex items-center justify-between text-muted-foreground mb-0.5">
                  <span className="font-medium">{c.author}</span>
                  <span>{c.createdAt ? formatDateTime(c.createdAt) : ''}</span>
                </div>
                <p className="text-foreground/80 whitespace-pre-wrap">{c.text}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Updates del pedido: historial + casilla para escribir (avisa por Slack) ──
function TicketUpdates({ ticket }: { ticket: any }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [msg, setMsg] = useState('');
  const addUpdate = trpc.tickets.addTicketUpdate.useMutation({
    onSuccess: () => {
      setMsg('');
      utils.tickets.getUserRequestTickets.invalidate();
    },
  });
  const updates: any[] = Array.isArray(ticket.updates) ? ticket.updates : [];

  const send = () => {
    const message = msg.trim();
    if (!message || addUpdate.isPending) return;
    addUpdate.mutate({ ticketId: ticket.ticketId, createdAt: ticket.createdAt, message });
  };

  return (
    <div className="border-t border-border pt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
      <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <MessageSquare className="h-3 w-3" /> {t('admin.userrequestskanban.updates')}{updates.length})
      </p>
      {updates.length > 0 && (
        <ul className="space-y-1.5">
          {updates.map((u, i) => (
            <li key={i} className="rounded bg-muted/50 p-2 text-[11px]">
              <div className="mb-0.5 flex items-center justify-between text-muted-foreground">
                <span className="font-medium">{u.byName || u.by}</span>
                <span>{u.at ? formatDateTime(u.at) : ''}</span>
              </div>
              <p className="whitespace-pre-wrap text-foreground/80">{u.message}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1.5">
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          rows={2}
          placeholder={t('admin.userrequestskanban.escribi_un_update_del_pedido')}
          className="min-h-0 flex-1 resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[11px] focus:border-brand-green focus:outline-hidden"
        />
        <button
          type="button"
          onClick={send}
          disabled={!msg.trim() || addUpdate.isPending}
          className="h-7 shrink-0 rounded-md bg-brand-green px-2.5 text-[11px] font-semibold text-black disabled:opacity-50"
        >
          {addUpdate.isPending ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground/70">
        {t('admin.userrequestskanban.supportia_le_avisa_por_slack_a', { name: ticket.createdByName || t('admin.userrequestskanban.quien_lo_cargo') })}
      </p>
      {addUpdate.error && (
        <p className="text-[10px] text-red-500">{addUpdate.error.message}</p>
      )}
    </div>
  );
}

// ── Card ──
function TicketCard({
  ticket,
  expanded,
  onToggle,
  dragHandleProps,
  dragging,
}: {
  ticket: any;
  expanded?: boolean;
  onToggle?: () => void;
  dragHandleProps?: Record<string, any>;
  dragging?: boolean;
}) {
  const { t } = useTranslation();
  const typeName = getFormTypeName((ticket.formType ?? ticket.type) as FormType);
  const body = ticketBody(ticket);
  const fields = expanded ? ticketFields(ticket) : [];
  const notionUrl = ticket.notionPageId
    ? `https://notion.so/${String(ticket.notionPageId).replace(/-/g, '')}`
    : null;

  return (
    <Card className={cn('p-3 space-y-2 select-none', dragging && 'shadow-lg ring-2 ring-[#1CC9B9] rotate-1')}>
      {/* Header: grip · título+tipo (click = expandir) · prioridad */}
      <div className="flex items-start gap-2">
        {dragHandleProps ? (
          <button
            {...dragHandleProps}
            aria-label="Arrastrar"
            className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : (
          <span className="mt-0.5 text-muted-foreground/40">
            <GripVertical className="h-4 w-4" />
          </span>
        )}

        <div
          className={cn('flex-1 min-w-0', onToggle && 'cursor-pointer')}
          onClick={onToggle}
        >
          <div className="flex items-center gap-1">
            {onToggle &&
              (expanded ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ))}
            <p className="text-sm font-medium leading-snug">{ticket.title}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 pl-[18px]">{typeName}</p>
        </div>

        {ticket.priority && (
          <span
            className={cn('shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded', priorityClass(ticket.priority))}
          >
            {ticket.priority}
          </span>
        )}
      </div>

      {/* Preview del cuerpo cuando está colapsada (tipo Notion) */}
      {!expanded && body && (
        <p
          className="text-xs text-muted-foreground pl-[26px]"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {body}
        </p>
      )}

      {/* Contenido expandido */}
      {expanded && (
        <div className="pl-[26px] pt-1 space-y-3">
          {body && <p className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">{body}</p>}

          {fields.length > 0 && (
            <dl className="space-y-1.5 border-t border-border pt-2">
              {fields.map(([label, value]) => (
                <div key={label} className="flex gap-2 text-xs">
                  <dt className="text-muted-foreground min-w-[104px] shrink-0">{label}</dt>
                  <dd className="text-foreground/80 wrap-break-word min-w-0">{value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex flex-col gap-1 border-t border-border pt-2 text-[11px] text-muted-foreground">
            {ticket.assignedTo && (
              <span className="flex items-center gap-1.5">
                <User className="h-3 w-3" /> {ticket.assignedTo}
              </span>
            )}
            {ticket.teamAsignado && (
              <span className="flex items-center gap-1.5">
                <Users className="h-3 w-3" /> {ticket.teamAsignado}
              </span>
            )}
            {ticket.deadline && (
              <span className="flex items-center gap-1.5">
                <CalendarClock className="h-3 w-3" /> Vence {formatDateTime(ticket.deadline)}
              </span>
            )}
          </div>

          <TicketUpdates ticket={ticket} />

          {ticket.notionPageId && (
            <div className="border-t border-border pt-2">
              <NotionDetail notionPageId={ticket.notionPageId} />
            </div>
          )}

          {notionUrl && (
            <a
              href={notionUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[11px] text-brand-green hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> {t('admin.userrequestskanban.ver_en_notion')}
            </a>
          )}
        </div>
      )}

      {/* Footer: solicitante · fecha */}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground pl-[26px]">
        <span className="truncate max-w-[60%]">{ticket.createdByName}</span>
        <span>{formatDateTime(ticket.createdAt)}</span>
      </div>
    </Card>
  );
}

function DraggableCard({
  ticket,
  expanded,
  onToggle,
}: {
  ticket: any;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.ticketId });
  return (
    <div ref={setNodeRef} className={cn(isDragging && 'opacity-40')}>
      <TicketCard
        ticket={ticket}
        expanded={expanded}
        onToggle={onToggle}
        dragHandleProps={{ ...listeners, ...attributes }}
      />
    </div>
  );
}

// ── Columna (droppable) ──
function Column({
  column,
  tickets,
  expandedIds,
  onToggle,
}: {
  column: { key: ColumnKey; label: string; dot: string };
  tickets: any[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  return (
    <div className="flex-1 min-w-[260px]">
      <div className="flex items-center gap-2 px-1 pb-2">
        <span className={cn('h-2 w-2 rounded-full', column.dot)} />
        <h3 className="text-sm font-semibold">{column.label}</h3>
        <span className="text-xs text-muted-foreground">{tickets.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'rounded-lg border border-dashed p-2 space-y-2 min-h-[120px] transition-colors',
          isOver ? 'border-brand-green bg-brand-green/5' : 'border-border bg-muted/30',
        )}
      >
        {tickets.map((tk) => (
          <DraggableCard
            key={tk.ticketId}
            ticket={tk}
            expanded={expandedIds.has(tk.ticketId)}
            onToggle={() => onToggle(tk.ticketId)}
          />
        ))}
        {tickets.length === 0 && (
          <p className="text-xs text-muted-foreground/60 text-center py-6">{t('admin.userrequestskanban.sin_tickets')}</p>
        )}
      </div>
    </div>
  );
}

export function UserRequestsKanbanPage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [hideClosed, setHideClosed] = useState<boolean>(
    () => localStorage.getItem(HIDE_CLOSED_KEY) === '1',
  );

  useEffect(() => {
    localStorage.setItem(HIDE_CLOSED_KEY, hideClosed ? '1' : '0');
  }, [hideClosed]);

  const { data, isLoading, error } = trpc.tickets.getUserRequestTickets.useQuery();

  const move = trpc.tickets.updateTicketKanbanState.useMutation({
    // Optimista: pinta la card en la nueva columna antes de que responda el server
    onMutate: async (vars) => {
      await utils.tickets.getUserRequestTickets.cancel();
      const prev = utils.tickets.getUserRequestTickets.getData();
      utils.tickets.getUserRequestTickets.setData(undefined, (old) =>
        old
          ? {
              ...old,
              data: old.data.map((tk: any) =>
                tk.ticketId === vars.ticketId ? { ...tk, kanbanEstado: vars.kanbanEstado } : tk,
              ),
            }
          : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) utils.tickets.getUserRequestTickets.setData(undefined, ctx.prev);
    },
    onSettled: () => {
      utils.tickets.getUserRequestTickets.invalidate();
    },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const tickets: any[] = data?.data ?? [];
  const activeTicket = activeId ? tickets.find((tk) => tk.ticketId === activeId) : null;

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  function handleDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const overCol = e.over?.id as ColumnKey | undefined;
    if (!overCol || !COLUMN_KEYS.has(overCol)) return;
    const ticket = tickets.find((tk) => tk.ticketId === String(e.active.id));
    if (!ticket) return;
    if (columnForTicket(ticket) === overCol) return;
    move.mutate({ ticketId: ticket.ticketId, createdAt: ticket.createdAt, kanbanEstado: overCol });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('tickets.page.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('errors.generic', { message: error.message })}</p>
      </div>
    );
  }

  const closedCount = tickets.filter((tk) => columnForTicket(tk) === 'done').length;
  const COLUMNS = COLUMN_DEFS.map((c) => ({ ...c, label: t(c.labelKey) }));
  const visibleColumns = hideClosed ? COLUMNS.filter((c) => c.key !== 'done') : COLUMNS;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('users.requests')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('admin.userrequestskanban.tickets_de_hardware_y_gestion_de')}
          </p>
        </div>
        <button
          onClick={() => setHideClosed((v) => !v)}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors"
        >
          {hideClosed ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {hideClosed ? t('admin.userrequestskanban.mostrar_cerrados', { n: closedCount }) : t('admin.userrequestskanban.ocultar_cerrados')}
        </button>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {visibleColumns.map((col) => (
            <Column
              key={col.key}
              column={col}
              tickets={tickets.filter((tk) => columnForTicket(tk) === col.key)}
              expandedIds={expandedIds}
              onToggle={toggleExpand}
            />
          ))}
        </div>
        <DragOverlay>{activeTicket ? <TicketCard ticket={activeTicket} dragging /> : null}</DragOverlay>
      </DndContext>
    </div>
  );
}
